import { Backdrop } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import LanguagesForm from "../../../../common/components/functional/languagesForm/LanguagesForm";
import LoadingSpinner from "../../../../common/components/functional/loadingSpinner/LoadingSpinner";
import isEmpty from "../../../../common/helpers/isEmpty";
import {
  Language,
  LanguageSkillCode,
  LanguageSkillLevel,
  Talent,
} from "../../../talents/talentAPI";
import {
  selectTalentById,
  updateTalentLanguages,
} from "../../../talents/talentsSlice";
import { UpdateTalentLanguages } from "../../../talents/types/commands";
import { EditMode } from "../../TalentDetails";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";

type FormValues = {
  id: string;
  languageCode: LanguageSkillCode;
  skillLevel: LanguageSkillLevel;
};

type Props = {
  editMode: EditMode;
  onClose: () => void;
};
const TalentEditLanguage = ({ onClose, editMode }: Props) => {
  const { t } = useTranslation();
  const { talentId } = useParams();
  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedLanguage] = useState<Language>(
    talent.languages?.values.find(
      (language) => language.id === editMode.id,
    ) as Language,
  );

  const defaultValues: FormValues = {
    id: editMode.id,
    languageCode: selectedLanguage.languageCode,
    skillLevel: selectedLanguage.skillLevel,
  };

  const methods = useForm<FormValues>({
    defaultValues: defaultValues,
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { errors, isDirty },
  } = methods;

  const onSubmit = (formValues: FormValues) => {
    setIsLoading(true);

    if (talentId) {
      const existingLanguages = talent.languages!!;

      const updatedLanguages: Language[] = existingLanguages.values.map(
        (language) => {
          if (language.id !== editMode.id) return language;

          return {
            id: language.id,
            languageCode: formValues.languageCode,
            skillLevel: formValues.skillLevel,
          };
        },
      );

      const commandPayload: UpdateTalentLanguages = {
        talentId: talentId,
        version: existingLanguages.version,
        languages: updatedLanguages,
      };

      dispatch(updateTalentLanguages(commandPayload)).then((action) => {
        setIsLoading(false);

        return isFulfilled(action) && onClose();
      });
    } else {
      setIsLoading(false);
    }
  };

  const onSave = () => handleSubmit(onSubmit)();
  const onCancel = () => onClose();
  const saveDisabled = !isEmpty(errors) || !isDirty || isLoading;

  return (
    <FormProvider {...methods}>
      <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
        <DialogContentWrapper
          title={t("languageSkill:language")}
          margin
          saveDisabled={saveDisabled}
          onSave={onSave}
          onCancel={onCancel}
        >
          <Backdrop
            open={isLoading}
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.4)",
              zIndex: 1000,
            }}
          >
            <LoadingSpinner />
          </Backdrop>
          <LanguagesForm disableLanguageSelect />
        </DialogContentWrapper>
      </UnsavedChangesProvider>
    </FormProvider>
  );
};

export default TalentEditLanguage;
